<template>
  <b-form @submit.prevent="onSave">
    <h5>Header Configuration</h5>
    <b-row>
      <b-col>
        <b-form-group label="Company">
          <b-form-select v-model="form.mrohCompId" :options="companies" value-field="compId" text-field="compName"
            required :disabled="!isEdit"></b-form-select>
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group label="Asset">
          <b-form-select v-model="form.mrohMassId" :options="assets" value-field="massId" text-field="massName"
            @change="onAssetChange" required></b-form-select>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-form-group label="Device">
          <b-form-select v-model="form.mrohDevcId" :options="devices" value-field="devcId" text-field="devcUniqueId"
            required></b-form-select>
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group label="Start Time">
          <b-form-input v-model="form.mrohStartTime" type="datetime-local" required></b-form-input>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Halaman status telah dihapus sesuai request -->

    <template v-if="isEdit">
      <hr />
      <h5>Detail Configuration</h5>

      <!-- Loading Animasi -->
      <div v-if="isLoadingDetails" class="text-center my-5">
        <b-spinner variant="primary" label="Loading details..."></b-spinner>
        <p class="mt-2 text-muted">Memuat data detail...</p>
      </div>

      <div v-else>
        <!-- Fungsi Duplicate -->
        <b-row class="mb-3 align-items-end">
          <b-col md="8">
            <b-form-group label="Duplicate Details From Existing Config" class="mb-0">
              <b-form-select v-model="selectedCopyHeader" :options="existingHeaders">
                <template #first>
                  <b-form-select-option :value="null" disabled>-- Select configuration to copy --</b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-button variant="outline-primary" @click="copyDetails" :disabled="!selectedCopyHeader" class="w-100">
              Copy Details
            </b-button>
          </b-col>
        </b-row>

        <b-table :items="details" :fields="detailFields" striped responsive="sm">
          <template #cell(mrodName)="data">
            <b-form-input v-model="data.item.mrodName" required></b-form-input>
          </template>
          <template #cell(mrodRpmMax)="data">
            <b-form-input v-model="data.item.mrodRpmMax" type="number" required></b-form-input>
          </template>
          <template #cell(mrodConsRate)="data">
            <b-form-input v-model="data.item.mrodConsRate" type="number" step="0.01" required></b-form-input>
          </template>
          <template #cell(actions)="data">
            <b-button variant="danger" size="sm" @click="removeDetail(data.index)">Remove</b-button>
          </template>
        </b-table>
        <b-button @click="addDetail">Add Detail</b-button>
      </div>
    </template>

    <div class="text-right mt-3">
      <b-button type="button" variant="secondary" @click="onCancel" class="mr-2">Cancel</b-button>
      <b-button type="submit" variant="primary" :disabled="isSaving || isLoadingDetails">
        <b-spinner v-if="isSaving" small></b-spinner>
        {{ isSaving ? 'Saving...' : 'Save' }}
      </b-button>
    </div>
  </b-form>
</template>

<script>
import CompanySvc from '@/services/CompanySvc';
import AssetSvc from '@/services/AssetSvc';
import DeviceSvc from '@/services/DeviceSvc';
import RpmOperationHeaderSvc from '@/services/RpmOperationHeaderSvc';
import RpmOperationDetailSvc from '@/services/RpmOperationDetailSvc';

export default {
  name: 'RpmClassificationForm',
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      form: {
        mrohCompId: null,
        mrohMassId: null,
        mrohDevcId: null,
        mrohStartTime: null,
        mrohStatus: 0,
      },
      companies: [],
      assets: [],
      devices: [],
      existingHeaders: [], // untuk list duplicate
      selectedCopyHeader: null,
      details: [],
      detailFields: [
        { key: 'mrodName', label: 'Name' },
        { key: 'mrodRpmMax', label: 'Max RPM' },
        { key: 'mrodConsRate', label: 'Consumption Rate' },
        { key: 'actions', label: 'Actions' },
      ],
      isSaving: false,
      isLoadingDetails: false,
      originalDetails: [],
    };
  },
  computed: {
    activeCompany() {
      return this.$store.state.auth.activeCompany;
    },
  },
  watch: {
    data: {
      async handler(val) {
        if (this.isEdit && val.mrohId) {
          this.form = {
            mrohId: val.mrohId,
            mrohCompId: val.mrohCompId,
            mrohMassId: val.mrohMassId,
            mrohDevcId: null, // akan diset setelah fetch devices
            mrohStartTime: val.mrohStartTime ? val.mrohStartTime.replace(' ', 'T') : null,
            mrohStatus: val.mrohStatus,
          };
          this.selectedCopyHeader = null;

          await this.fetchDevices(val.mrohMassId);
          this.form.mrohDevcId = val.mrohDevcId; // Kembalikan valuenya setelah device keload

          await this.fetchDetails(val.mrohId);
        } else {
          this.form = {
            mrohCompId: this.activeCompany ? this.activeCompany.compId : null,
            mrohMassId: null,
            mrohDevcId: null,
            mrohStartTime: new Date().toISOString().slice(0, 16),
            mrohStatus: 0,
          };
          this.devices = [];
          this.details = [];
          this.originalDetails = [];
          this.selectedCopyHeader = null;
        }
      },
      immediate: true,
    },
  },
  async mounted() {
    await this.fetchInitialData();
  },
  methods: {
    async fetchInitialData() {
      try {
        const compParam = this.activeCompany ? this.activeCompany.compId : null;
        // Hapus pemanggilan DeviceSvc karena device bergantung pada asset yang dipilih
        const [companyRes, assetRes, headerRes] = await Promise.all([
          CompanySvc.getCompanies(),
          AssetSvc.getAssets(),
          RpmOperationHeaderSvc.getRpmOperationHeaders(compParam ? { 'finds[mrohCompId]': compParam } : {}),
        ]);
        this.companies = companyRes.data.data;
        this.assets = assetRes.data.data;

        // Mempersiapkan data untuk combobox duplicate filter header
        this.existingHeaders = headerRes.data.map((h) => ({
          value: h.mrohId,
          text: `${h.mrohMass ? h.mrohMass.massName : 'Unknown Asset'} - Start: ${h.mrohStartTime}`,
        }));
      } catch (error) {
        console.error('Failed to fetch initial data for form', error);
        this.$toast.error('Failed to load data for form fields.');
      }
    },
    async onAssetChange(massId) {
      this.form.mrohDevcId = null;
      if (massId) {
        await this.fetchDevices(massId);
      } else {
        this.devices = [];
      }
    },
    async fetchDevices(massId) {
      try {
        const response = await DeviceSvc.getDevices({
          params: {
            'finds[devcMassId]': massId,
            'finds[devcType]': 'rpm',
          },
        });
        this.devices = response.data.data;
      } catch (error) {
        console.error('Failed to fetch devices', error);
        this.devices = [];
        this.$toast.error('Failed to fetch devices based on Asset.');
      }
    },
    async fetchDetails(headerId) {
      this.isLoadingDetails = true;
      try {
        const response = await RpmOperationDetailSvc.getRpmOperationDetailsByHeaderId(headerId, { page: 1, size: 100, 'orders[mrodRpmMax]': 'asc' });

        // Ekstraksi data secara bullet-proof dari response API
        let detailData = [];
        if (response && Array.isArray(response)) {
          detailData = response;
        } else if (response && Array.isArray(response.data)) {
          detailData = response.data;
        } else if (response && response.data && Array.isArray(response.data.data)) {
          detailData = response.data.data;
        }

        // Format angka desimal agar trailing nol hilang
        detailData = detailData.map((d) => ({
          ...d,
          mrodRpmMax: d.mrodRpmMax ? Number(d.mrodRpmMax) : 0,
          mrodConsRate: d.mrodConsRate ? Number(d.mrodConsRate) : 0,
        }));

        this.details = detailData;
        this.originalDetails = JSON.parse(JSON.stringify(detailData));
      } catch (error) {
        console.error('Failed to fetch details', error);
        // Set kembali jadi array agar tidak error
        this.details = [];
        this.originalDetails = [];
        this.$toast.error('Failed to fetch details.');
      } finally {
        this.isLoadingDetails = false;
      }
    },
    async copyDetails() {
      if (!this.selectedCopyHeader) return;
      try {
        const response = await RpmOperationDetailSvc.getRpmOperationDetailsByHeaderId(this.selectedCopyHeader);

        let detailData = [];
        if (response && Array.isArray(response)) {
          detailData = response;
        } else if (response && Array.isArray(response.data)) {
          detailData = response.data;
        } else if (response && response.data && Array.isArray(response.data.data)) {
          detailData = response.data.data;
        }

        // Push ke details tapi mrodId dihilangkan agar di-treat sebagai data baru saat di Save
        const copiedItems = detailData.map((d) => ({
          mrodName: d.mrodName,
          mrodRpmMax: d.mrodRpmMax ? Number(d.mrodRpmMax) : 0,
          mrodConsRate: d.mrodConsRate ? Number(d.mrodConsRate) : 0,
        }));

        // Safeguard buat array state
        if (!Array.isArray(this.details)) {
          this.details = [];
        }

        this.details.push(...copiedItems);
        this.$toast.success('Detail berhasil disalin. Klik Save untuk menyimpan.');
      } catch (error) {
        console.error('Failed to copy details', error);
        this.$toast.error('Gagal menyalin detail.');
      }
    },
    addDetail() {
      // Safeguard buat array state sebelum push
      if (!Array.isArray(this.details)) {
        this.details = [];
      }
      this.details.push({
        mrodName: '',
        mrodRpmMax: 0,
        mrodConsRate: 0,
      });
    },
    removeDetail(index) {
      if (Array.isArray(this.details)) {
        this.details.splice(index, 1);
      }
    },
    async onSave() {
      this.isSaving = true;
      try {
        const headerPayload = { ...this.form };
        if (headerPayload.mrohStartTime) {
          headerPayload.mrohStartTime = headerPayload.mrohStartTime.replace('T', ' ');
        }

        let headerId;
        if (this.isEdit) {
          headerId = headerPayload.mrohId;
          await RpmOperationHeaderSvc.updateRpmOperationHeader(headerId, headerPayload);
        } else {
          const result = await RpmOperationHeaderSvc.createRpmOperationHeader(headerPayload);
          headerId = result.id || result.data?.id || result.mrohId;
        }

        const detailPromises = [];

        const safeDetails = Array.isArray(this.details) ? this.details : [];
        const safeOriginals = Array.isArray(this.originalDetails) ? this.originalDetails : [];

        // Detail baru (belum punya mrodId)
        const newDetails = safeDetails.filter((d) => !d.mrodId);

        // Detail yang di-update
        const updatedDetails = safeDetails.filter((d) => {
          if (!d.mrodId) return false;
          const original = safeOriginals.find((od) => String(od.mrodId) === String(d.mrodId));
          return original && JSON.stringify(original) !== JSON.stringify(d);
        });

        // Detail yang di-remove
        const deletedDetailIds = safeOriginals
          .filter((od) => !safeDetails.some((d) => String(d.mrodId) === String(od.mrodId)))
          .map((od) => od.mrodId);

        newDetails.forEach((detail) => {
          detailPromises.push(RpmOperationDetailSvc.createRpmOperationDetail({
            ...detail,
            mrodMrohId: headerId,
          }));
        });

        updatedDetails.forEach((detail) => {
          // Perbaikan: Clone object dan hapus properti mrodMroh agar tidak dikirim ke backend
          const payload = { ...detail };
          delete payload.mrodMroh;

          detailPromises.push(RpmOperationDetailSvc.updateRpmOperationDetail(payload.mrodId, payload));
        });

        deletedDetailIds.forEach((id) => {
          if (typeof RpmOperationDetailSvc.deleteRpmOperationDetail === 'function') {
            detailPromises.push(RpmOperationDetailSvc.deleteRpmOperationDetail(id));
          } else if (typeof RpmOperationDetailSvc.delete === 'function') {
            detailPromises.push(RpmOperationDetailSvc.delete(id));
          } else if (typeof RpmOperationDetailSvc.destroy === 'function') {
            detailPromises.push(RpmOperationDetailSvc.destroy(id));
          }
        });

        await Promise.all(detailPromises);

        this.$toast.success('Successfully saved configuration.');
        this.$emit('onSaved', headerId);
      } catch (error) {
        console.error('Failed to save configuration', error);
        this.$toast.error('Failed to save configuration.');
      } finally {
        this.isSaving = false;
      }
    },
    onCancel() {
      this.$emit('onCancel');
    },
  },
};
</script>
